import { RotateCcw } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { TradeIdeasOutboxItem } from "@/lib/tradeideas/types";

export function OutboxTable({
  items,
  status,
  pending,
  error,
  onStatusChange,
  onRetry,
}: {
  items: TradeIdeasOutboxItem[];
  status: string;
  pending: boolean;
  error: string | null;
  onStatusChange: (status: string) => void;
  onRetry: () => void;
}) {
  const statuses = ["dead", "retry", "pending"];
  return (
    <section className="overflow-hidden rounded-md border border-border">
      <div className="flex items-center justify-between gap-3 border-b border-border bg-muted/30 px-3 py-2">
        <div>
          <h2 className="text-sm font-semibold">Outbox</h2>
          <p className="text-xs text-muted-fg">Dead deliveries that need retry or inspection.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-border p-1">
            {statuses.map((item) => (
              <Button
                key={item}
                type="button"
                size="sm"
                variant={status === item ? "default" : "ghost"}
                onClick={() => onStatusChange(item)}
              >
                {item}
              </Button>
            ))}
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onRetry}
            disabled={pending || items.length === 0}
          >
            <RotateCcw className="size-4" />
            Retry
          </Button>
        </div>
      </div>
      {error ? (
        <div className="px-3 py-4 text-sm text-red-200">{error}</div>
      ) : items.length === 0 ? (
        <div className="px-3 py-6 text-center text-sm text-muted-fg">
          No dead deliveries.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-[90px_140px_100px_90px_1.4fr_160px] border-b border-border px-3 py-2 text-[11px] font-medium uppercase tracking-wider text-muted-fg">
            <span>ID</span>
            <span>Sink</span>
            <span>Status</span>
            <span>Attempts</span>
            <span>Error</span>
            <span>Updated</span>
          </div>
          {items.map((item) => (
            <div
              key={item.id}
              className="grid grid-cols-[90px_140px_100px_90px_1.4fr_160px] items-center gap-3 border-b border-border/60 px-3 py-2 text-sm last:border-b-0"
            >
              <span className="font-mono text-xs">#{item.id}</span>
              <span className="truncate font-medium">{item.sink}</span>
              <Badge variant={item.status === "dead" ? "danger" : "warn"}>
                {item.status}
              </Badge>
              <span className="tabular-nums">{item.attempt_count}</span>
              <span className="truncate text-xs text-muted-fg">{item.last_error || "-"}</span>
              <span className="text-xs text-muted-fg">{formatTime(item.updated_at)}</span>
            </div>
          ))}
        </>
      )}
    </section>
  );
}

function formatTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}
